import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Snackbar, useMediaQuery, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import HandoutImageLoader from '../handout/handout-image-loader';
import UserApi from '../../../rest-api/user-api';
import { Link, useNavigate } from 'react-router-dom';
import BinArchiveIcon from '../../../assets/icons/BinArchive.svg';
import arrowRightIcon from '../../../assets/icons/Arrow-white_Right.svg';
import shareIcon from '../../../assets/icons/ShareHandoutFlight.svg';
import QuickShareDialog from '../handout/quick-share-dialog';
import { parseUserHandouts, stringifyUserHandouts } from '../../../utils/handouts-utils';

function Handouts() {
    const { authStore, userDataStore, contentStore, imageStore } = useContext(StoreContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [handouts, setHandouts] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarStatus, setSnackbarStatus] = useState('success');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [quickShareOpen, setQuickShareOpen] = useState(false);
    const cardsPerView = 4;
    const canScrollLeft = currentIndex > 0;
    const canScrollRight = currentIndex + cardsPerView < handouts.length;
    const hasMoreThanCards = handouts.length > cardsPerView;

    useEffect(() => {
        // Safely parse handouts, default to empty array if undefined or not parseable
        const handoutsData = parseUserHandouts(userDataStore.userData.handouts);

        if (userDataStore.userData.handouts !== stringifyUserHandouts(handoutsData)) {
            userDataStore.updateUserHandouts(handoutsData);
        }

        const handoutIds = handoutsData.map((handout) => handout.contentId);

        const fetchedHandouts = contentStore.getContentFromIds(handoutIds).map((handout) => {
            const userDataHandout = handoutsData.find((data) => data.contentId === handout.contentId);
            if (userDataHandout) {
                return {
                    ...handout,
                    formattedCategories: userDataHandout.categories.split(',').map((category) => category.trim()),
                };
            }
            return handout;
        });
        setHandouts(fetchedHandouts);
    }, [userDataStore.userData.handouts, contentStore, userDataStore]);

    useEffect(() => {
        if (currentIndex > 0 && currentIndex >= handouts.length) {
            setCurrentIndex(Math.max(0, handouts.length - cardsPerView));
        }
    }, [currentIndex, handouts.length]);

    const handleDeleteHandout = async (handoutData) => {
        let isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser' ? true : false;

        const currentHandoutsData = parseUserHandouts(userDataStore.userData.handouts);
        const updatedHandoutsData = currentHandoutsData.filter(
            (handout) => handout.contentId !== handoutData.contentId,
        );

        let payload = {
            handouts: updatedHandoutsData,
        };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }

            const handoutIds = updatedHandoutsData.map((handout) => handout.contentId);
            const fetchedHandouts = contentStore.getContentFromIds(handoutIds).map((handout) => {
                const userDataHandout = updatedHandoutsData.find((data) => data.contentId === handout.contentId);
                if (userDataHandout) {
                    return {
                        ...handout,
                        formattedCategories: userDataHandout.categories.split(',').map((category) => category.trim()),
                    };
                }
                return handout;
            });

            userDataStore.updateUserHandouts(updatedHandoutsData);
            setSnackbarMessage('Handout deleted from bundle');
            setSnackbarStatus('success');
            setSnackbarOpen(true);
            setHandouts(fetchedHandouts);
        } catch (error) {
            console.error('Error deleting handout from bundle: ', error);
            setSnackbarMessage('Failed to delete handout from bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const handleOpenHandout = (handoutData) => {
        localStorage.removeItem('loadSavedHandout');
        localStorage.removeItem('loadCustomisedHandout');
        localStorage.removeItem('loadCustomisedHandoutInEditMode');
        localStorage.removeItem('openInCustomiseMode');
        navigate(`/handout/${handoutData.contentId}`);
    };

    const getSnackbarIcon = () => {
        return snackbarStatus === 'success' ? (
            <CheckIcon sx={{ ml: 1, color: 'white' }} />
        ) : (
            <CloseIcon sx={{ ml: 1, color: 'red' }} />
        );
    };

    return (
        <Box
            className="handouts-container"
            sx={{
                background: '#F0F0FC',
                maxWidth: '1440px',
                margin: '0 auto',
                width: '100%',
                px: { xs: '16px', md: '40px' },
                pt: { xs: '24px', md: '39px' },
                pb: { xs: handouts.length > 0 ? '120px' : '24px', md: handouts.length > 0 ? '32px' : '32px' },
                boxSizing: 'border-box',
                minHeight: 'calc(100vh - 83px)',
            }}
        >
            <Typography
                sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '24px', md: '40px' },
                    lineHeight: { xs: '24px', md: '40px' },
                    letterSpacing: { xs: '-0.96px', md: '-1.6px' },
                    color: '#000453',
                    mb: { xs: '22px', md: '28px' },
                }}
            >
                Handout bundle ({handouts.length})
            </Typography>

            {handouts.length === 0 ? (
                <Box
                    sx={{
                        background: '#FFFFFF',
                        borderRadius: '30px',
                        p: { xs: '20px', md: '28px' },
                    }}
                >
                    <Typography sx={{ color: '#000453' }}>
                        You have yet to save any handouts to your profile! <br />
                        <Link to="/search">Search for conditions here</Link>
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        background: '#FFFFFF',
                        borderRadius: '30px',
                        p: { xs: '20px 12px 16px', md: '21px 21px 15px' },
                        position: 'relative',
                        overflow: 'visible',
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {!isMobile && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: '-46px',
                                    zIndex: 10,
                                    width: 50,
                                    height: 50,
                                    borderRadius: '50%',
                                    background: hasMoreThanCards && canScrollLeft ? '#FF669B' : '#FFB3CE',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: hasMoreThanCards && canScrollLeft ? 'pointer' : 'default',
                                    ...(hasMoreThanCards && canScrollLeft && { '&:hover': { background: '#FF85B0' } }),
                                }}
                                onClick={() => canScrollLeft && setCurrentIndex((i) => i - 1)}
                            >
                                <img
                                    src={arrowRightIcon}
                                    alt="Previous"
                                    width={18}
                                    style={{ transform: 'scaleX(-1)', display: 'block' }}
                                />
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                                gap: { xs: '20px', md: '21px' },
                                width: '100%',
                            }}
                        >
                            {(isMobile ? handouts : handouts.slice(currentIndex, currentIndex + cardsPerView)).map(
                                (handoutData) => {
                                    const locale = 'en-UK';
                                    const localeData = handoutData.contentData?.[locale] || [];
                                    const illustrationBlock = localeData
                                        .flatMap((section) => section.blocks)
                                        .find((block) => block.type === 'illustration');
                                    const imageSrc = handoutData.previewImage || illustrationBlock?.src || null;

                                    return (
                                        <Box
                                            key={handoutData.contentId}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => handleOpenHandout(handoutData)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    handleOpenHandout(handoutData);
                                                }
                                            }}
                                            sx={{
                                                background: '#F0F0FC',
                                                borderRadius: '20px',
                                                p: { xs: '14px', md: '21px' },
                                                minHeight: { xs: '411px', md: '414px' },
                                                display: 'flex',
                                                flexDirection: 'column',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    background: '#FFFFFF',
                                                    borderRadius: '10px',
                                                    height: { xs: '254px', md: '322px' },
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <HandoutImageLoader
                                                    src={imageSrc}
                                                    alt={handoutData.contentName}
                                                    imageStore={imageStore}
                                                    handoutData={handoutData}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        display: 'block',
                                                    }}
                                                />
                                            </Box>

                                            <Typography
                                                sx={{
                                                    mt: '10px',
                                                    minHeight: '56px',
                                                    fontFamily: 'Inter, sans-serif',
                                                    fontWeight: 600,
                                                    fontSize: { xs: '22px', md: '22px' },
                                                    lineHeight: { xs: '26px', md: '26px' },
                                                    letterSpacing: { xs: '-0.44px', md: '-0.44px' },
                                                    color: '#000453',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {handoutData.contentName}
                                            </Typography>

                                            <Box sx={{ mt: 'auto' }}>
                                                <Box
                                                    sx={{
                                                        height: '2px',
                                                        background: 'rgba(0, 4, 83, 0.10)',
                                                        mb: '8px',
                                                    }}
                                                />
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <Box
                                                        component="button"
                                                        aria-label="delete"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleDeleteHandout(handoutData);
                                                        }}
                                                        onKeyDown={(event) => {
                                                            event.stopPropagation();
                                                        }}
                                                        sx={{
                                                            border: 'none',
                                                            background: 'transparent',
                                                            padding: 0,
                                                            display: 'flex',
                                                            cursor: 'pointer',
                                                            borderRadius: '50%',
                                                        }}
                                                    >
                                                        <img
                                                            src={BinArchiveIcon}
                                                            alt="Delete handout"
                                                            width={50}
                                                            height={50}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                },
                            )}
                        </Box>

                        {!isMobile && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: '-46px',
                                    zIndex: 10,
                                    width: 50,
                                    height: 50,
                                    borderRadius: '50%',
                                    background: hasMoreThanCards && canScrollRight ? '#FF669B' : '#FFB3CE',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: hasMoreThanCards && canScrollRight ? 'pointer' : 'default',
                                    ...(hasMoreThanCards && canScrollRight && { '&:hover': { background: '#FF85B0' } }),
                                }}
                                onClick={() => canScrollRight && setCurrentIndex((i) => i + 1)}
                            >
                                <img src={arrowRightIcon} alt="Next" width={18} style={{ display: 'block' }} />
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            {handouts.length > 0 && (
                <Box
                    sx={{
                        position: isMobile ? 'fixed' : 'static',
                        left: isMobile ? 0 : 'auto',
                        right: isMobile ? 0 : 'auto',
                        bottom: isMobile ? 0 : 'auto',
                        zIndex: isMobile ? 1200 : 'auto',
                        background: isMobile ? '#FFFFFF' : 'transparent',
                        borderTop: isMobile ? '1px solid #E5E5E5' : 'none',
                        height: isMobile ? '85px' : 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isMobile ? 'center' : 'flex-start',
                        mt: isMobile ? 0 : '35px',
                        '@media print': { display: 'none' },
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: '1440px',
                            width: '100%',
                            px: { xs: '19px', md: 0 },
                            py: { xs: '18px', md: '12px' },
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                borderRadius: { xs: '188px', md: '200px' },
                                background: '#FF669B',
                                color: '#fff',
                                minHeight: { xs: '47px', md: '50px' },
                                px: { xs: '18.8px', md: '20px' },
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500,
                                fontSize: { xs: '14px', md: '18px' },
                                lineHeight: { xs: '14px', md: '18px' },
                                letterSpacing: { xs: '-0.28px', md: '-0.36px' },
                                textTransform: 'none',
                                boxShadow: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: { xs: '9.4px', md: '10px' },
                                '&:hover': { background: '#FF85B0', boxShadow: 'none' },
                            }}
                            onClick={() => setQuickShareOpen(true)}
                        >
                            <img
                                src={shareIcon}
                                alt=""
                                width={isMobile ? 17 : 22}
                                style={{ filter: 'brightness(0) invert(1)' }}
                            />
                            <span>Share {handouts.length} handout(s)</span>
                        </Box>
                    </Box>
                </Box>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={
                    <Box display="flex" alignItems="center">
                        {snackbarMessage}
                        {getSnackbarIcon()} {/* Dynamically selected icon based on the snackbar status */}
                    </Box>
                }
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        backgroundImage: 'linear-gradient(132deg, #FA96C3 -2.07%, #FF669B 15.82%, #BD8DE8 116.64%)',
                        display: 'flex',
                        justifyContent: 'center',
                        borderRadius: 2,
                    },
                }}
            />

            {handouts.length > 0 && (
                <QuickShareDialog open={quickShareOpen} onClose={() => setQuickShareOpen(false)} handout={handouts} />
            )}
        </Box>
    );
}

export default Handouts;
